import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, ThumbsUp, ThumbsDown, Globe, Minimize2, Maximize2 } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [language, setLanguage] = useState('en');
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize session ID
  useEffect(() => {
    const existingSession = localStorage.getItem('chat_session_id');
    if (existingSession) {
      setSessionId(existingSession);
      loadChatHistory(existingSession);
    } else {
      const newSession = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSession);
      localStorage.setItem('chat_session_id', newSession);
    }
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  const loadChatHistory = async (sid) => {
    try {
      const res = await axios.get(`${API}/chat/history/${sid}`);
      if (res.data.messages && res.data.messages.length > 0) {
        const formattedMessages = res.data.messages.map(msg => ({
          text: msg.content,
          sender: msg.role === 'user' ? 'user' : 'bot',
          timestamp: msg.timestamp
        }));
        setMessages(formattedMessages);
      } else {
        // Show welcome message
        setMessages([{
          text: getWelcomeMessage(language),
          sender: 'bot',
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (err) {
      console.error('Failed to load chat history', err);
      setMessages([{
        text: getWelcomeMessage(language),
        sender: 'bot',
        timestamp: new Date().toISOString()
      }]);
    }
  };

  const getWelcomeMessage = (lang) => {
    const welcomes = {
      en: "👋 Hi! I'm here to help with labour law compliance. What can I assist you with today?",
      hi: "👋 नमस्ते! मैं श्रम कानून अनुपालन में मदद के लिए यहां हूं। आज मैं आपकी कैसे सहायता कर सकता हूं?",
      ta: "👋 வணக்கம்! தொழிலாளர் சட்ட இணக்கத்தில் உதவ நான் இங்கே இருக்கிறேன். இன்று நான் உங்களுக்கு எப்படி உதவ முடியும்?",
      kn: "👋 ನಮಸ್ಕಾರ! ಕಾರ್ಮಿಕ ಕಾನೂನು ಅನುಸರಣೆಯಲ್ಲಿ ಸಹಾಯ ಮಾಡಲು ನಾನು ಇಲ್ಲಿದ್ದೇನೆ. ಇಂದು ನಾನು ನಿಮಗೆ ಹೇಗೆ ಸಹಾಯ ಮಾಡಬಹುದು?",
      te: "👋 నమస్కారం! కార్మిక చట్ట సమ్మతిలో సహాయం చేయడానికి నేను ఇక్కడ ఉన్నాను. ఈరోజు నేను మీకు ఎలా సహాయం చేయగలను?",
      ml: "👋 നമസ്കാരം! തൊഴിൽ നിയമ പാലനത്തിൽ സഹായിക്കാൻ ഞാൻ ഇവിടെയുണ്ട്. ഇന്ന് ഞാൻ നിങ്ങളെ എങ്ങനെ സഹായിക്കും?"
    };
    return welcomes[lang] || welcomes.en;
  };

  const getQuickActions = (lang) => {
    const actions = {
      en: [
        "What services do you offer?",
        "Book a consultation",
        "View compliance resources",
        "POSH compliance help"
      ],
      hi: [
        "आप कौन सी सेवाएं प्रदान करते हैं?",
        "परामर्श बुक करें",
        "अनुपालन संसाधन देखें",
        "POSH अनुपालन सहायता"
      ],
      ta: [
        "நீங்கள் என்ன சேவைகளை வழங்குகிறீர்கள்?",
        "ஆலோசனை முன்பதிவு",
        "இணக்க ஆதாரங்களைக் காண்க",
        "POSH இணக்க உதவி"
      ]
    };
    return actions[lang] || actions.en;
  };

  const handleSendMessage = async (text = null) => {
    const messageText = text || inputMessage.trim();
    if (!messageText || !sessionId) return;

    // Add user message
    const userMsg = {
      text: messageText,
      sender: 'user',
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);
    setInputMessage('');
    setIsTyping(true);

    // Get current page context
    const pageContext = window.location.pathname;

    try {
      const res = await axios.post(`${API}/chat/message`, {
        message: messageText,
        session_id: sessionId,
        language: language,
        page_context: pageContext
      });

      // Add bot response
      const botMsg = {
        text: res.data.reply,
        sender: 'bot',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, botMsg]);

      // Show feedback after 3rd message
      if (messages.length >= 5 && !feedbackGiven) {
        setShowFeedback(true);
      }
    } catch (err) {
      console.error('Chat error:', err);
      const errorMsg = {
        text: "Sorry, I'm having trouble connecting. Please try again or contact us directly.",
        sender: 'bot',
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleFeedback = async (rating) => {
    try {
      await axios.post(`${API}/chat/feedback`, {
        session_id: sessionId,
        rating: rating
      });
      setShowFeedback(false);
      setFeedbackGiven(true);
    } catch (err) {
      console.error('Feedback error:', err);
    }
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
    const welcomeMsg = {
      text: getWelcomeMessage(lang),
      sender: 'bot',
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, welcomeMsg]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-[#0B1F3A] to-[#1a3a5c] text-white rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all duration-300 flex items-center justify-center z-50 group"
        aria-label="Open chat"
      >
        <MessageCircle size={28} className="group-hover:rotate-12 transition-transform" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#C9A646] rounded-full animate-pulse" />
      </button>
    );
  }

  return (
    <div className={`fixed ${isMinimized ? 'bottom-6 right-6' : 'bottom-6 right-6'} z-50 transition-all duration-300`}>
      <div className={`bg-white rounded-2xl shadow-2xl border border-gray-200 ${isMinimized ? 'w-80' : 'w-96'} ${isMinimized ? 'h-16' : 'h-[600px]'} flex flex-col overflow-hidden`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0B1F3A] to-[#1a3a5c] p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#C9A646] rounded-full flex items-center justify-center">
              <MessageCircle size={20} className="text-[#0B1F3A]" />
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm">Labour Law Assistant</h3>
              <p className="text-xs text-gray-300">Always here to help</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Language Selector */}
            <div className="relative group">
              <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <Globe size={18} className="text-white" />
              </button>
              <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg py-2 hidden group-hover:block min-w-[120px]">
                <button onClick={() => changeLanguage('en')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100">English</button>
                <button onClick={() => changeLanguage('hi')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100">हिंदी</button>
                <button onClick={() => changeLanguage('ta')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100">தமிழ்</button>
                <button onClick={() => changeLanguage('kn')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100">ಕನ್ನಡ</button>
                <button onClick={() => changeLanguage('te')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100">తెలుగు</button>
                <button onClick={() => changeLanguage('ml')} className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100">മലയാളം</button>
              </div>
            </div>
            <button onClick={() => setIsMinimized(!isMinimized)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              {isMinimized ? <Maximize2 size={18} className="text-white" /> : <Minimize2 size={18} className="text-white" />}
            </button>
            <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X size={18} className="text-white" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.sender === 'user'
                      ? 'bg-[#0B1F3A] text-white rounded-br-sm'
                      : msg.isError
                      ? 'bg-red-50 text-red-600 border border-red-200 rounded-bl-sm'
                      : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                    {msg.sender === 'bot' && !msg.isError && (
                      <p className="text-xs mt-2 opacity-60">
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              {messages.length <= 1 && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 text-center">Quick actions:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {getQuickActions(language).map((action, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSendMessage(action)}
                        className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs hover:border-[#C9A646] hover:bg-[#C9A646]/5 transition-colors text-left"
                      >
                        {action}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Feedback */}
              {showFeedback && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-900 mb-2">How helpful was this conversation?</p>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map(rating => (
                      <button
                        key={rating}
                        onClick={() => handleFeedback(rating)}
                        className="px-3 py-1 bg-white border border-blue-300 rounded-lg text-sm hover:bg-blue-100 transition-colors"
                      >
                        {'⭐'.repeat(rating)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 bg-white border-t border-gray-200">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A646] text-sm"
                />
                <button
                  onClick={() => handleSendMessage()}
                  disabled={!inputMessage.trim()}
                  className="px-4 py-3 bg-[#0B1F3A] text-white rounded-xl hover:bg-[#0B1F3A]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send size={18} />
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-2 text-center">
                Powered by AI • <a href="/contact" className="text-[#C9A646] hover:underline">Contact Us</a>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Chatbot;
